import React, { useMemo, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ArrowUturnLeftIcon } from '@heroicons/react/20/solid';
import FormButton from '@ui/button/FormButton';
import Message from '@ui/message/Message';
import DashboardPlaceholder from '@app/component/placeholder/DashboardPlaceholder';
import type { BiProperty, RevenueScope, SegmentationView, VatBasis } from '@app/model/bi/Bi';
import { useSegmentGroups } from '@app/model/bi/service/BiHooks';
import BiPageHeader from '../fragment/BiPageHeader';
import SegmentGroupsDrawer, { SegmentGroupsDrawerHandle } from '../fragment/SegmentGroupsDrawer';
import useWideScreen from '../useWideScreen';
import { formatDateTime, periodPresets, scopeValues } from '../Bi.utilities';
import { useAccountBiScope, useBiAddress } from '../BiScopeHooks';
import { CompareItem, compareChoices, CompareMeasure, itemIdOfScope, itemsOfAddress, itemsParam, reportSearch } from '../compare/Compare.utilities';
import { compareOn } from '../compare/compareSwitch';
import { useCompareReports } from '../compare/CompareQueries';
import CompareItemsMenu from '../compare/CompareItemsMenu';
import CompareChartCard from '../compare/CompareChartCard';
import CompareTableCard from '../compare/CompareTableCard';
import { useBiWidthClass } from '../BiFullWidth';
import BiSwitchRow from '../fragment/BiSwitchRow';
import BiViewBy from '../fragment/BiViewBy';
import useBiView from '../useBiView';

// The BI Property comparison of the Segmentation report (ticket 1 of the comparison, direction A): 2 to 6 items (a property,
// a Property group or All properties) side by side, with the period and the switches of the report (D1–D21). The page
// asks the CURRENT Segmentation queries once for each item (T1). The report page gets the button "Compare properties"
// and the places of D23/D24 (the import time in the sublabel, "Edit groups" in View by); the other reports do not change.
// The address: the query of the report (hotel, group, period, revenue switches) plus ?items=…; "Back to report" opens the
// report with that query (D13). View by is the shared choice of the report (the browser keeps it).
const BiSegmentationComparePage = () => {
	const { search } = useLocation();
	const scope = useAccountBiScope();
	const address = useBiAddress(scope.data);
	const segmentGroups = useSegmentGroups();
	const hasGroups = (segmentGroups.data?.groups.length ?? 0) > 0;
	const { view: shown, setView } = useBiView('SEGMENTATION', hasGroups);
	const view: SegmentationView = shown === 'GROUPS' ? 'GROUPS' : 'SEGMENTS';
	const [measure, setMeasure] = useState<CompareMeasure>('rn');
	const [share, setShare] = useState(false);
	const [row, setRow] = useState<{ key: string, title: string } | null>(null);
	const drawer = useRef<SegmentGroupsDrawerHandle>(null);
	const wide = useWideScreen();
	const width = useBiWidthClass();

	const choices = useMemo(() => scope.data === undefined ? [] : compareChoices(scope.data, address.groups), [scope.data, address.groups]);
	const { items, dropped } = useMemo(() => itemsOfAddress(new URLSearchParams(search).get('items'), choices,
		itemIdOfScope(address.companyHotelId, address.propertyGroupId)), [search, choices, address.companyHotelId, address.propertyGroupId]);
	const ready = scope.data !== undefined && segmentGroups.data !== undefined && address.ready && items.length > 0;
	const reports = useCompareReports(items, address.period.from, address.period.to, view, address.includeOptions, ready);

	if (!compareOn()) return <Navigate to={`/secured/bi/segmentation${reportSearch(search)}`} replace />;
	if (scope.isError || segmentGroups.isError) return <main className={`${width} px-4 py-6 sm:px-6 lg:px-8`}>
		<Message state="error" title="The Property comparison did not load" flush>Reload the page. If the problem stays, contact RoomPulse.</Message>
	</main>;
	if (scope.data === undefined || segmentGroups.data === undefined) return <main className={`${width} px-4 py-6 sm:px-6 lg:px-8`}><DashboardPlaceholder /></main>;

	const s = scope.data;
	const setItems = (next: CompareItem[]) => {
		address.set({ items: itemsParam(next) });
		setRow(null);
	};
	// The properties of the items, for "Reservations as of": the oldest import of these properties (each one is that fresh).
	const propertiesOf = (item: CompareItem): BiProperty[] => item.companyHotelId !== null ? s.properties.filter(p => p.companyHotelId === item.companyHotelId)
		: (item.propertyGroupId !== null ? (address.groups.find(g => g.id === item.propertyGroupId)?.properties ?? []) : s.properties);
	const importedAt = scopeValues(items.flatMap(propertiesOf)).importedAt;
	// D24, as on the report: the import time in the sublabel (its own line on a phone); the right of the header holds the action.
	const imported = importedAt === null ? null : formatDateTime(importedAt);
	const g = segmentGroups.data;
	const failed = items.filter((_, i) => reports[i]?.state === 'error');
	const loading = items.filter((_, i) => reports[i]?.state === 'loading');
	const { first, last } = address;
	const presets = periodPresets(address.asOfMonth, first, last);
	const groupOrder = (g.groups ?? []).map(group => String(group.id));

	return <>
		<BiPageHeader scope={s} companyHotelId={address.companyHotelId} search={address.search} selectable={false} title="Property comparison"
			sublabel={`${items.length} compared · Segmentation${wide && imported !== null ? ` · Reservations as of ${imported}` : ''}`} page="Property comparison · Segmentation" fullWidthSwitch>
			{!wide && imported !== null && <span className="whitespace-nowrap text-sm text-gray-500">Reservations as of{' '}
				<span className="font-semibold text-gray-900">{imported}</span></span>}
			<FormButton state="white" to={`/secured/bi/segmentation${reportSearch(search)}`}
				icon={<ArrowUturnLeftIcon aria-hidden="true" className="h-4 w-4 text-gray-500" />}>Back to report</FormButton>
		</BiPageHeader>
		<main className={`${width} flex flex-col gap-5 px-4 py-6 sm:px-6 lg:px-8`}>
			<CompareItemsMenu choices={choices} items={items} onChange={setItems} />
			{items.length === 1 && <Message state="info" title="Add a second property or group" flush>
				<p>The comparison starts with the scope of the report. Open “Compare” and add a property, a Property group or All properties: up to 6.</p>
			</Message>}
			{dropped > 0 && <Message state="warning" flush
				title={dropped === 1 ? 'One property or group of the link is not in the comparison' : `${dropped} properties or groups of the link are not in the comparison`}>
				<p>{dropped === 1 ? 'You cannot read it in BI, it has no reservations, or the link holds more than 6.' : 'You cannot read them in BI, they have no reservations, or the link holds more than 6.'}</p>
			</Message>}
			{/* One message for each column that failed while others show; when all fail, the cards show the error. */}
			{failed.length < items.length && failed.map(item => <Message key={item.id} state="error" title={`${item.label} did not load`} flush>
				<p>The other columns show.{' '}
					<FormButton state="text" isLink size="small" onClick={() => reports[items.indexOf(item)]?.refetch()}>Try again</FormButton></p>
			</Message>)}
			{loading.length > 0 && loading.length < items.length && <p className="text-sm text-gray-500">
				{loading.map(i => i.label).join(', ')} {loading.length === 1 ? 'loads' : 'load'}… (3 columns load at the same time at most)</p>}
			{/* The switch row of the BI reports (ROO2-283), with no source: the items can have different sources (grill Q3). */}
			<BiSwitchRow scope={address.revenueScope} vat={address.vat} onScope={(value: RevenueScope) => address.set({ revenue: value === 'TOTAL' ? 'total' : 'room' })}
				onVat={(value: VatBasis) => address.set({ vat: value === 'NET' ? 'net' : 'incl' })} options={address.options} onOptions={address.setOptions}
				period={address.period}
				onPeriod={range => address.set({ from: range.from, to: range.to })} presets={presets} min={first} max={last} sources={[]}>
				{/* D23, as on the report: "Edit groups" in the View by frame; it opens the Segment Groups editor of the report. */}
				<BiViewBy value={shown} onChange={next => { setView(next); setRow(null); }} hasGroups={hasGroups} singleLabel="PMS segments"
					label="Rows of the comparison" onEdit={g.canEdit ? () => drawer.current?.open() : null} editLabel="Edit groups" />
			</BiSwitchRow>
			<CompareChartCard items={items} period={address.period} view={view} scope={address.revenueScope} vat={address.vat} row={row} includeOptions={address.includeOptions}
				enabled={ready} />
			<CompareTableCard items={items} reports={reports} groupOrder={groupOrder} period={address.period} view={view} scope={address.revenueScope}
				vat={address.vat} asOf={s.asOf} measure={measure} onMeasure={setMeasure} share={share} onShare={setShare}
				selectedRow={row?.key ?? null} onSelectRow={setRow} />
		</main>
		{g.canEdit && <SegmentGroupsDrawer ref={drawer} data={g} companyName={s.companyName} propertyCount={s.companyProperties} />}
	</>;
};

export default BiSegmentationComparePage;
